import { Component, ChangeDetectionStrategy, input, output, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import {
  UpdateImpactResponse,
  UpdateImpactWarning,
  UpdateImpactWarningType,
  TypeRemovalDetails,
  UnitReductionDetails,
} from '../../../../models';

const BLOCKING_TYPES = new Set<UpdateImpactWarningType>([
  UpdateImpactWarningType.TYPE_REMOVAL_BLOCKED,
  UpdateImpactWarningType.UNIT_REDUCTION_BLOCKED,
]);

export interface ResolvedWarning {
  type: UpdateImpactWarningType;
  message: string;
  blocking: boolean;
}

@Component({
  selector: 'app-update-impact-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    MessageModule,
    ProgressSpinnerModule,
    TranslocoModule,
  ],
  templateUrl: './update-impact-dialog.component.html',
  styleUrl: './update-impact-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateImpactDialogComponent {
  private readonly transloco = inject(TranslocoService);

  readonly visible = input.required<boolean>();
  readonly impact = input<UpdateImpactResponse | null>(null);
  readonly loading = input(false);

  readonly visibleChange = output<boolean>();
  readonly proceed = output<void>();
  readonly cancel = output<void>();

  readonly resolvedWarnings = computed<ResolvedWarning[]>(() => {
    const imp = this.impact();
    if (!imp) return [];
    return imp.warnings.map(w => this.resolveWarning(w));
  });

  readonly blockingWarnings = computed(() =>
    this.resolvedWarnings().filter(w => w.blocking),
  );

  readonly safeWarnings = computed(() =>
    this.resolvedWarnings().filter(w => !w.blocking),
  );

  readonly hasBlockingWarnings = computed(() => this.blockingWarnings().length > 0);

  readonly canProceed = computed(() => {
    const imp = this.impact();
    return imp ? imp.safe : false;
  });

  onClose(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  onProceed(): void {
    this.proceed.emit();
  }

  private resolveWarning(warning: UpdateImpactWarning): ResolvedWarning {
    const blocking = BLOCKING_TYPES.has(warning.type);
    const key = `objects.wizard.updateImpact.warnings.${warning.type}`;
    const params = this.buildParams(warning);

    return {
      type: warning.type,
      message: this.transloco.translate(key, params),
      blocking,
    };
  }

  private buildParams(warning: UpdateImpactWarning): Record<string, unknown> {
    switch (warning.type) {
      case UpdateImpactWarningType.TYPE_REMOVAL_BLOCKED:
      case UpdateImpactWarningType.TYPE_REMOVAL_SAFE: {
        const d = warning.details as TypeRemovalDetails;
        return { typeName: d.typeName, reservationCount: d.reservationCount };
      }
      case UpdateImpactWarningType.UNIT_REDUCTION_BLOCKED:
      case UpdateImpactWarningType.UNIT_REDUCTION_SAFE: {
        const d = warning.details as UnitReductionDetails;
        return {
          typeName: d.typeName,
          currentQuantity: d.currentQuantity,
          newQuantity: d.newQuantity,
          affectedReservations: d.affectedReservations,
        };
      }
      case UpdateImpactWarningType.UNIT_REDUCTION_WARN: {
        const d = warning.details as UnitReductionDetails;
        return {
          typeName: d.typeName,
          currentQuantity: d.currentQuantity,
          newQuantity: d.newQuantity,
          futureReservations: d.futureReservations ?? 0,
          pastReservations: d.pastReservations ?? 0,
        };
      }
    }
  }
}
